import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Trash2, AlertTriangle, CheckCircle, Clock, Ban, Settings2, Coins, Receipt, Shield } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { apiRequest, queryClient } from "@/lib/queryClient";
import AdminAddonBalances from "./AdminAddonBalances";
import AdminAddonTransactions from "./AdminAddonTransactions";
import AdminBalanceAdjustment from "./AdminBalanceAdjustment";
import AdminBadges from "./AdminBadges";
import AdminNavigation from "@/components/AdminNavigation";
import AdminFooter from "@/components/AdminFooter";

interface LifetimeCoupon {
  id: number;
  couponCode: string;
  email: string;
  userId: number | null;
  status: string;
  redeemedAt: string | null;
  revokedAt: string | null;
  revokedBy: string | null;
  createdAt: string;
  userFirstName: string | null;
  userLastName: string | null;
  userEmail: string | null;
}

export default function AdminDashboard() {
  const { toast } = useToast();
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState<LifetimeCoupon | null>(null);
  const [activeTab, setActiveTab] = useState("coupons");
  const [adjustmentUserId, setAdjustmentUserId] = useState<number | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  // Fetch all lifetime coupons
  const { data: coupons, isLoading, error } = useQuery<LifetimeCoupon[]>({
    queryKey: ["/api/admin/lifetime-coupons"],
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Revoke coupon mutation
  const revokeMutation = useMutation({
    mutationFn: async (couponId: number) => {
      const response = await apiRequest("DELETE", `/api/admin/lifetime-coupons/${couponId}`);
      if (!response.ok) {
        throw new Error(await response.text());
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/lifetime-coupons"] });
      toast({
        title: "Coupon Revoked",
        description: "The lifetime coupon has been successfully revoked.",
      });
      setRevokeDialogOpen(false);
      setSelectedCoupon(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: `Failed to revoke coupon: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <Badge variant="outline" className="text-orange-600"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case "redeemed":
        return <Badge variant="default" className="bg-green-600"><CheckCircle className="w-3 h-3 mr-1" />Redeemed</Badge>;
      case "revoked":
        return <Badge variant="destructive"><Ban className="w-3 h-3 mr-1" />Revoked</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const handleRevoke = (coupon: LifetimeCoupon) => {
    setSelectedCoupon(coupon);
    setRevokeDialogOpen(true);
  };

  const confirmRevoke = () => {
    if (selectedCoupon) {
      revokeMutation.mutate(selectedCoupon.id);
    }
  };

  const handleSelectUser = (userId: number) => {
    setSelectedUserId(userId);
    // Could navigate to user detail view or open user info modal
    // For now, just switch to balances tab and highlight the user
    setActiveTab("balances");
  };

  const handleAdjustBalance = (userId: number) => {
    setAdjustmentUserId(userId);
  };

  const handleCloseAdjustment = () => {
    setAdjustmentUserId(null);
  };

  const handleAdjustmentSuccess = () => {
    setAdjustmentUserId(null);
    toast({
      title: "Success",
      description: "User balance has been adjusted successfully.",
    });
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="space-y-4">
          <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
          <div className="h-64 bg-gray-200 rounded animate-pulse"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-center text-red-600">
              <AlertTriangle className="w-5 h-5 mr-2" />
              Error loading lifetime coupons: {error instanceof Error ? error.message : "Unknown error"}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const pendingCoupons = coupons?.filter(c => c.status === "pending") || [];
  const redeemedCoupons = coupons?.filter(c => c.status === "redeemed") || [];
  const revokedCoupons = coupons?.filter(c => c.status === "revoked") || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50" data-testid="admin-dashboard">
      <AdminNavigation currentPage="Management" showBackButton={true} />
      
      <div className="max-w-7xl mx-auto p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-6 md:space-y-8">
        <div className="flex flex-col space-y-4 md:flex-row md:items-center md:justify-between md:space-y-0">
          <div>
            <p className="text-sm md:text-base text-muted-foreground">Manage lifetime coupons, user balances, and transactions</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className="text-orange-600 text-xs">
              <Clock className="w-3 h-3 mr-1" />
              <span className="hidden sm:inline">Pending:</span> {pendingCoupons.length}
            </Badge>
            <Badge variant="default" className="bg-green-600 text-xs">
              <CheckCircle className="w-3 h-3 mr-1" />
              <span className="hidden sm:inline">Redeemed:</span> {redeemedCoupons.length}
            </Badge>
            <Badge variant="destructive" className="text-xs">
              <Ban className="w-3 h-3 mr-1" />
              <span className="hidden sm:inline">Revoked:</span> {revokedCoupons.length}
            </Badge>
          </div>
        </div>

        {/* Main Admin Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 bg-white/50 backdrop-blur-sm border border-primary shadow-sm h-auto" data-testid="admin-tabs">
            <TabsTrigger value="coupons" className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 p-2 sm:p-3 text-xs sm:text-sm min-h-[3rem] sm:min-h-0" data-testid="coupons-tab">
              <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
              <span className="text-center leading-tight">
                <span className="block sm:hidden">Coupons</span>
                <span className="hidden sm:inline">Lifetime Coupons</span>
              </span>
            </TabsTrigger>
            <TabsTrigger value="balances" className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 p-2 sm:p-3 text-xs sm:text-sm min-h-[3rem] sm:min-h-0" data-testid="balances-tab">
              <Coins className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
              <span className="text-center leading-tight">
                <span className="block sm:hidden">Balances</span>
                <span className="hidden sm:inline">User Balances</span>
              </span>
            </TabsTrigger>
            <TabsTrigger value="badges" className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 p-2 sm:p-3 text-xs sm:text-sm min-h-[3rem] sm:min-h-0" data-testid="badges-tab">
              <Shield className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
              <span className="text-center leading-tight">
                <span className="block sm:hidden">Badges</span>
                <span className="hidden sm:inline">Badge Management</span>
              </span>
            </TabsTrigger>
            <TabsTrigger value="transactions" className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 p-2 sm:p-3 text-xs sm:text-sm min-h-[3rem] sm:min-h-0" data-testid="transactions-tab">
              <Receipt className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
              <span className="text-center leading-tight">
                <span className="block sm:hidden">Analytics</span>
                <span className="hidden sm:inline">Transactions & Analytics</span>
              </span>
            </TabsTrigger>
          </TabsList>

          {/* Lifetime Coupons Tab */}
          <TabsContent value="coupons" className="space-y-6" data-testid="coupons-content">

            {/* Coupons Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg hover:shadow-xl transition-shadow duration-200">
                <CardHeader className="pb-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center">
                      <Clock className="h-4 w-4 text-white" />
                    </div>
                    <CardTitle className="text-sm font-medium text-foreground">Pending Coupons</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">{pendingCoupons.length}</div>
                  <p className="text-xs text-muted-foreground">Awaiting user registration</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg hover:shadow-xl transition-shadow duration-200">
                <CardHeader className="pb-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
                      <CheckCircle className="h-4 w-4 text-white" />
                    </div>
                    <CardTitle className="text-sm font-medium text-foreground">Redeemed Coupons</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">{redeemedCoupons.length}</div>
                  <p className="text-xs text-muted-foreground">Successfully activated</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg hover:shadow-xl transition-shadow duration-200">
                <CardHeader className="pb-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-red-500 to-rose-500 flex items-center justify-center">
                      <Ban className="h-4 w-4 text-white" />
                    </div>
                    <CardTitle className="text-sm font-medium text-foreground">Revoked Coupons</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold bg-gradient-to-r from-red-600 to-rose-600 bg-clip-text text-transparent">{revokedCoupons.length}</div>
                  <p className="text-xs text-muted-foreground">Manually disabled</p>
                </CardContent>
              </Card>
            </div>

            {/* Coupons Table */}
            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent">All Lifetime Coupons</CardTitle>
              </CardHeader>
              <CardContent>
                {!coupons || coupons.length === 0 ? (
                  <div className="text-center text-muted-foreground py-8">
                    No lifetime coupons found.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-2">Email</th>
                          <th className="text-left p-2">Status</th>
                          <th className="text-left p-2">User</th>
                          <th className="text-left p-2">Redeemed Date</th>
                          <th className="text-left p-2">Created</th>
                          <th className="text-left p-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {coupons.map((coupon) => (
                          <tr key={coupon.id} className="border-b hover:bg-gray-50" data-testid={`coupon-row-${coupon.id}`}>
                            <td className="p-2">
                              <div className="font-medium">{coupon.email}</div>
                              <div className="text-sm text-muted-foreground">{coupon.couponCode}</div>
                            </td>
                            <td className="p-2">
                              {getStatusBadge(coupon.status)}
                            </td>
                            <td className="p-2">
                              {coupon.userFirstName && coupon.userLastName ? (
                                <div>
                                  <div className="font-medium">{coupon.userFirstName} {coupon.userLastName}</div>
                                  <div className="text-sm text-muted-foreground">{coupon.userEmail}</div>
                                </div>
                              ) : (
                                <span className="text-muted-foreground">Not registered</span>
                              )}
                            </td>
                            <td className="p-2">
                              {coupon.redeemedAt ? (
                                new Date(coupon.redeemedAt).toLocaleDateString()
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </td>
                            <td className="p-2">
                              {new Date(coupon.createdAt).toLocaleDateString()}
                            </td>
                            <td className="p-2">
                              {coupon.status !== "revoked" && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleRevoke(coupon)}
                                  className="text-red-600 hover:text-red-700"
                                  disabled={revokeMutation.isPending}
                                  data-testid={`revoke-coupon-${coupon.id}`}
                                >
                                  <Trash2 className="w-3 h-3 mr-1" />
                                  Revoke
                                </Button>
                              )}
                              {coupon.status === "revoked" && coupon.revokedBy && (
                                <div className="text-sm text-muted-foreground">
                                  Revoked by {coupon.revokedBy}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* User Balances Tab */}
          <TabsContent value="balances" className="space-y-6" data-testid="balances-content">
            {adjustmentUserId ? (
              <AdminBalanceAdjustment
                userId={adjustmentUserId}
                onClose={handleCloseAdjustment}
                onSuccess={handleAdjustmentSuccess}
              />
            ) : (
              <AdminAddonBalances
                onSelectUser={handleSelectUser}
                onAdjustBalance={handleAdjustBalance}
              />
            )}
          </TabsContent>

          {/* Badge Management Tab */}
          <TabsContent value="badges" className="space-y-6" data-testid="badges-content">
            <AdminBadges />
          </TabsContent>

          {/* Transactions & Analytics Tab */}
          <TabsContent value="transactions" className="space-y-6" data-testid="transactions-content">
            <AdminAddonTransactions
              onSelectUser={handleSelectUser}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Revoke Confirmation Dialog */}
      <AlertDialog open={revokeDialogOpen} onOpenChange={setRevokeDialogOpen}>
        <AlertDialogContent data-testid="revoke-coupon-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke Lifetime Coupon</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to revoke the lifetime coupon for <strong>{selectedCoupon?.email}</strong>?
              
              {selectedCoupon?.status === "redeemed" && (
                <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <div className="flex items-center text-yellow-800">
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    <strong>Warning:</strong>
                  </div>
                  <p className="text-sm text-yellow-700 mt-1">
                    This coupon has been redeemed. Revoking it will change the user's subscription status from lifetime back to trial.
                  </p>
                </div>
              )}
              
              <p className="mt-2 text-sm text-muted-foreground">
                This action cannot be undone.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-revoke-button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRevoke}
              className="bg-red-600 hover:bg-red-700"
              disabled={revokeMutation.isPending}
              data-testid="confirm-revoke-button"
            >
              {revokeMutation.isPending ? "Revoking..." : "Revoke Coupon"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      <AdminFooter />
    </div>
  );
}